import { useMemo, useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import type {
  CommunityPollCreate,
  CommunityPollOptionResponse,
  CommunityPollResponse,
} from "@bw/shared/types/generated";
import type { useCommunityGameNights } from "../hooks/useCommunityGameNights";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

type Hook = ReturnType<typeof useCommunityGameNights>;
type ChatAudience = "members" | "attendees" | "attendees_and_waitlisted";

const headingSx = { fontSize: "1rem", fontWeight: 700 } as const;

function toLocalInput(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function slotWindowLabel(startsAt: string, endsAt: string, timezone: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
    return "Time unavailable";
  }
  const startLabel = start.toLocaleString(undefined, {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  const endLabel = end.toLocaleTimeString(undefined, {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  });
  return `${startLabel} – ${endLabel}`;
}

function votes(count: number): string {
  return `${count} ${count === 1 ? "vote" : "votes"}`;
}

interface CandidateSlotDraft {
  label: string;
  startsAt: string;
  endsAt: string;
}

function CreatePollDialog({
  open,
  onClose,
  createPoll,
  ownerFacilityId,
}: {
  open: boolean;
  onClose: () => void;
  createPoll: Hook["createPoll"];
  ownerFacilityId?: string | null;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const defaultSlots = useMemo<CandidateSlotDraft[]>(() => {
    const day1 = new Date(Date.now() + 48 * 60 * 60 * 1000);
    const day2 = new Date(Date.now() + 72 * 60 * 60 * 1000);
    return [day1, day2].map((day, index) => ({
      label: `Option ${index + 1}`,
      startsAt: toLocalInput(day),
      endsAt: toLocalInput(new Date(day.getTime() + 2 * 60 * 60 * 1000)),
    }));
  }, []);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [useCommunityFacility, setUseCommunityFacility] = useState(Boolean(ownerFacilityId));
  const [customLocation, setCustomLocation] = useState("");
  const [capacity, setCapacity] = useState("12");
  const [courtCount, setCourtCount] = useState("2");
  const [chatAudience, setChatAudience] = useState<ChatAudience>("attendees");
  const [slots, setSlots] = useState<CandidateSlotDraft[]>(defaultSlots);

  const valid =
    Boolean(title.trim()) &&
    (useCommunityFacility || Boolean(customLocation.trim())) &&
    Number(capacity) >= 2 &&
    Number(courtCount) >= 1 &&
    slots.length >= 2 &&
    slots.every(
      (slot) =>
        Boolean(slot.label.trim()) &&
        Boolean(slot.startsAt) &&
        Boolean(slot.endsAt) &&
        new Date(slot.endsAt) > new Date(slot.startsAt),
    );

  function updateSlot(index: number, patch: Partial<CandidateSlotDraft>) {
    setSlots((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
  }

  function addSlot() {
    if (slots.length >= 6) return;
    const base = new Date(Date.now() + (slots.length + 2) * 24 * 60 * 60 * 1000);
    setSlots((prev) => [
      ...prev,
      {
        label: `Option ${prev.length + 1}`,
        startsAt: toLocalInput(base),
        endsAt: toLocalInput(new Date(base.getTime() + 2 * 60 * 60 * 1000)),
      },
    ]);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid || createPoll.isPending) return;
    const payload: CommunityPollCreate = {
      title: title.trim(),
      description: description.trim() || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      facility_id: useCommunityFacility ? (ownerFacilityId ?? null) : null,
      custom_location: useCommunityFacility ? null : customLocation.trim(),
      court_count: Number(courtCount),
      capacity: Number(capacity),
      guest_limit_per_member: 0,
      skill_guidance: null,
      chat_audience: chatAudience,
      options: slots.map((slot) => ({
        label: slot.label.trim(),
        starts_at: new Date(slot.startsAt).toISOString(),
        ends_at: new Date(slot.endsAt).toISOString(),
      })),
    };
    createPoll.mutate(payload, {
      onSuccess: () => {
        setTitle("");
        setDescription("");
        onClose();
      },
    });
  }

  return (
    <Dialog open={open} onClose={createPoll.isPending ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit}>
        <DialogTitle sx={{ pr: 7 }}>
          Create a time poll
          <IconButton
            aria-label="Close"
            onClick={onClose}
            disabled={createPoll.isPending}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {createPoll.isError ? (
              <GuardianAwareError error={createPoll.error}>
                <Alert severity="error">
                  We couldn&apos;t create that poll. Check the candidate times and try again.
                </Alert>
              </GuardianAwareError>
            ) : null}
            <TextField label="Poll title" value={title} onChange={(event) => setTitle(event.target.value)} required autoFocus />
            <TextField
              label="Notes (optional)"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              multiline
              minRows={2}
            />
            {ownerFacilityId ? (
              <FormControlLabel
                control={
                  <Switch checked={useCommunityFacility} onChange={(event) => setUseCommunityFacility(event.target.checked)} />
                }
                label="Use this community’s facility"
              />
            ) : null}
            {!useCommunityFacility ? (
              <TextField
                label="Meeting place"
                value={customLocation}
                onChange={(event) => setCustomLocation(event.target.value)}
                required
              />
            ) : null}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Players"
                type="number"
                value={capacity}
                onChange={(event) => setCapacity(event.target.value)}
                slotProps={{ htmlInput: { min: 2, max: 500 } }}
                fullWidth
              />
              <TextField
                label="Courts"
                type="number"
                value={courtCount}
                onChange={(event) => setCourtCount(event.target.value)}
                slotProps={{ htmlInput: { min: 1, max: 100 } }}
                fullWidth
              />
            </Stack>
            <TextField
              select
              label="Event chat access when scheduled"
              value={chatAudience}
              onChange={(event) => setChatAudience(event.target.value as ChatAudience)}
              fullWidth
            >
              <MenuItem value="members">All community members</MenuItem>
              <MenuItem value="attendees">Confirmed attendees only</MenuItem>
              <MenuItem value="attendees_and_waitlisted">Confirmed attendees &amp; waitlist</MenuItem>
            </TextField>
            <Divider />
            <Typography component="h3" sx={headingSx}>
              Candidate times
            </Typography>
            <Stack divider={<Divider />} spacing={1.5}>
              {slots.map((slot, index) => (
                <Stack key={index} spacing={1.5}>
                  <TextField
                    label={`Option ${index + 1} label`}
                    size="small"
                    value={slot.label}
                    onChange={(event) => updateSlot(index, { label: event.target.value })}
                    required
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      label={`Option ${index + 1} starts`}
                      type="datetime-local"
                      size="small"
                      value={slot.startsAt}
                      onChange={(event) => updateSlot(index, { startsAt: event.target.value })}
                      required
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <TextField
                      label={`Option ${index + 1} ends`}
                      type="datetime-local"
                      size="small"
                      value={slot.endsAt}
                      onChange={(event) => updateSlot(index, { endsAt: event.target.value })}
                      required
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Stack>
                </Stack>
              ))}
            </Stack>
            {slots.length < 6 ? (
              <Button size="small" onClick={addSlot} startIcon={<AddRounded />} sx={{ alignSelf: "flex-start" }}>
                Add time option
              </Button>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={createPoll.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!valid || createPoll.isPending}>
            {createPoll.isPending ? "Creating…" : "Publish poll"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

/** Converting closes the poll, publishes the event and signs voters up, so it is confirmed. */
function ConvertPollDialog({
  poll,
  onClose,
  convertPoll,
}: {
  poll: CommunityPollResponse | null;
  onClose: () => void;
  convertPoll: Hook["convertPoll"];
}) {
  const totalVotes = poll?.options.reduce((sum, opt) => sum + opt.vote_count, 0) ?? 0;
  // With votes, start from the leading time; with none, the organizer must choose.
  const [chosen, setChosen] = useState<string | null>(null);
  const [lastPollId, setLastPollId] = useState<string | null>(null);
  if (!poll && lastPollId !== null) {
    setLastPollId(null);
  } else if (poll && poll.poll_id !== lastPollId) {
    setLastPollId(poll.poll_id);
    setChosen(totalVotes > 0 ? (poll.winning_option_id ?? null) : null);
  }
  const option = poll?.options.find((item) => item.option_id === chosen) ?? null;
  const pending = convertPoll.isPending;
  const failed = convertPoll.isError && convertPoll.variables?.pollId === poll?.poll_id;
  return (
    <Dialog open={Boolean(poll)} onClose={pending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Turn this time into an event?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {failed ? (
            <Alert severity="error">We couldn&apos;t create the event. The poll is still open.</Alert>
          ) : null}
          {totalVotes === 0 ? (
            <Typography variant="body2">Nobody has voted yet. Pick the time to schedule.</Typography>
          ) : null}
          <RadioGroup
            aria-label="Time to schedule"
            value={chosen ?? ""}
            onChange={(change) => setChosen(change.target.value)}
          >
            {poll?.options.map((opt) => (
              <FormControlLabel
                key={opt.option_id}
                value={opt.option_id}
                control={<Radio size="small" />}
                sx={{ alignItems: "flex-start", py: 0.5 }}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {opt.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {slotWindowLabel(opt.starts_at, opt.ends_at, poll.timezone)} · {votes(opt.vote_count)}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          {option ? (
            <Typography variant="body2" color="text.secondary">
              {option.vote_count > 0
                ? `${option.vote_count} ${option.vote_count === 1 ? "member" : "members"} picked ${option.label}. Voters who are still members will be signed up and notified. Anyone past capacity goes on the waitlist. `
                : `Nobody picked ${option.label}, so only you will be signed up. `}
              The poll closes and this can&apos;t be undone.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={pending}>
          Keep poll open
        </Button>
        <Button
          variant="contained"
          disabled={!poll || !option || pending}
          onClick={() =>
            poll &&
            option &&
            convertPoll.mutate(
              { pollId: poll.poll_id, data: { option_id: option.option_id, publish: true } },
              { onSuccess: onClose },
            )
          }
        >
          {pending ? "Creating…" : "Create event"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PollOptionRow({
  poll,
  opt,
  busy,
  saving,
  onToggle,
}: {
  poll: CommunityPollResponse;
  opt: CommunityPollOptionResponse;
  busy: boolean;
  saving: boolean;
  onToggle: (optionId: string) => void;
}) {
  const isOpen = poll.status === "open";
  const leading = poll.winning_option_id === opt.option_id && opt.vote_count > 0;
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", py: 0.75 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
          {opt.label}
          {leading ? (
            <Typography component="span" variant="caption" sx={{ color: "primary.main", fontWeight: 700, ml: 1 }}>
              Top pick
            </Typography>
          ) : null}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {slotWindowLabel(opt.starts_at, opt.ends_at, poll.timezone)} · {votes(opt.vote_count)}
        </Typography>
      </Box>
      {isOpen ? (
        <Button
          size="small"
          variant={opt.voted_by_me ? "contained" : "outlined"}
          aria-pressed={opt.voted_by_me}
          aria-label={`Available ${opt.label}`}
          disabled={busy}
          startIcon={opt.voted_by_me ? <CheckRounded fontSize="small" /> : undefined}
          onClick={() => onToggle(opt.option_id)}
          sx={{ borderRadius: 999, flexShrink: 0 }}
        >
          {saving ? "Saving…" : "Available"}
        </Button>
      ) : null}
    </Stack>
  );
}

function PollRow({
  poll,
  votePoll,
  convertPoll,
  onConvertRequest,
}: {
  poll: CommunityPollResponse;
  votePoll: Hook["votePoll"];
  convertPoll: Hook["convertPoll"];
  onConvertRequest: (poll: CommunityPollResponse) => void;
}) {
  const [savingOptionId, setSavingOptionId] = useState<string | null>(null);
  const isOpen = poll.status === "open";
  const voting = votePoll.isPending && votePoll.variables?.pollId === poll.poll_id;
  const converting = convertPoll.isPending && convertPoll.variables?.pollId === poll.poll_id;
  const busy = voting || converting;
  const voteFailed = votePoll.isError && votePoll.variables?.pollId === poll.poll_id;
  const myOptionIds = poll.options.filter((opt) => opt.voted_by_me).map((opt) => opt.option_id);

  function toggle(optionId: string) {
    if (!isOpen || busy) return;
    const next = myOptionIds.includes(optionId)
      ? myOptionIds.filter((id) => id !== optionId)
      : [...myOptionIds, optionId];
    setSavingOptionId(optionId);
    votePoll.mutate(
      { pollId: poll.poll_id, data: { option_ids: next } },
      { onSettled: () => setSavingOptionId(null) },
    );
  }

  return (
    <Box component="article" aria-labelledby={`poll-title-${poll.poll_id}`} sx={{ py: 1.25, px: { xs: 0.5, sm: 0 } }}>
      <Typography
        variant="caption"
        sx={{ color: isOpen ? "primary.main" : "text.secondary", fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" }}
      >
        {isOpen ? "Voting open" : "Scheduled"}
      </Typography>
      <Typography id={`poll-title-${poll.poll_id}`} component="h3" sx={{ fontWeight: 700, fontSize: "1rem", overflowWrap: "anywhere" }}>
        {poll.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
        {poll.location_label} · {poll.capacity} players · {poll.court_count} {poll.court_count === 1 ? "court" : "courts"}
      </Typography>
      {poll.description ? (
        <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
          {poll.description}
        </Typography>
      ) : null}
      <Stack divider={<Divider />} sx={{ mt: 0.5 }}>
        {poll.options.map((opt) => (
          <PollOptionRow
            key={opt.option_id}
            poll={poll}
            opt={opt}
            busy={busy}
            saving={voting && savingOptionId === opt.option_id}
            onToggle={toggle}
          />
        ))}
      </Stack>
      {voteFailed ? (
        <Box sx={{ mt: 0.5 }}>
          <GuardianAwareError error={votePoll.error}>
            <Typography role="alert" variant="body2" color="error.main">
              Your answer didn&apos;t save. Try again.
            </Typography>
          </GuardianAwareError>
        </Box>
      ) : null}
      {isOpen && poll.can_manage ? (
        <Button size="small" disabled={busy} onClick={() => onConvertRequest(poll)} sx={{ mt: 0.5 }}>
          Turn winning time into an event
        </Button>
      ) : null}
    </Box>
  );
}

function ReminderPreference({
  preferences,
  updatePreferences,
}: {
  preferences: Hook["preferences"];
  updatePreferences: Hook["updatePreferences"];
}) {
  // Show the change at once; on failure the switch returns to the saved value.
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  if (preferences.isError) {
    return (
      <Alert
        severity="warning"
        role="status"
        action={<Button onClick={() => void preferences.refetch()}>Retry</Button>}
      >
        Reminder settings are unavailable right now.
      </Alert>
    );
  }
  if (!preferences.data) return null;
  const saved = preferences.data.event_reminders_enabled;
  return (
    <Box sx={{ px: { xs: 0.5, sm: 0 } }}>
      <FormControlLabel
        control={
          <Switch
            checked={optimistic ?? saved}
            disabled={updatePreferences.isPending}
            onChange={(change) => {
              const next = change.target.checked;
              setOptimistic(next);
              updatePreferences.mutate(
                { event_reminders_enabled: next },
                { onSettled: () => setOptimistic(null) },
              );
            }}
          />
        }
        label="Event reminders"
      />
      <Typography variant="body2" color="text.secondary">
        Cancellations and schedule changes always reach you.
      </Typography>
      {updatePreferences.isError ? (
        <Typography role="alert" variant="body2" color="error.main" sx={{ mt: 0.5 }}>
          Your reminder setting didn&apos;t save. Try again.
        </Typography>
      ) : null}
    </Box>
  );
}

export function CommunityCoordinationTools({
  ownerFacilityId,
  polls,
  createPoll,
  votePoll,
  convertPoll,
  preferences,
  updatePreferences,
}: {
  ownerFacilityId?: string | null;
  polls: Hook["polls"];
  createPoll: Hook["createPoll"];
  votePoll: Hook["votePoll"];
  convertPoll: Hook["convertPoll"];
  preferences: Hook["preferences"];
  updatePreferences: Hook["updatePreferences"];
}) {
  const [creatingPoll, setCreatingPoll] = useState(false);
  const [converting, setConverting] = useState<CommunityPollResponse | null>(null);
  const canCreate = Boolean(polls.data?.can_create);
  const items = polls.data?.items ?? [];

  return (
    <Stack spacing={2.5}>
      <Stack component="section" spacing={0.5} aria-labelledby="community-time-polls">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between", px: { xs: 0.5, sm: 0 } }}>
          <Typography id="community-time-polls" component="h2" sx={headingSx}>
            Time polls
          </Typography>
          {canCreate ? (
            <Button size="small" startIcon={<AddRounded />} onClick={() => setCreatingPoll(true)}>
              New poll
            </Button>
          ) : null}
        </Stack>
        {polls.isError ? (
          <Alert
            severity="warning"
            role="status"
            action={<Button onClick={() => void polls.refetch()}>Retry</Button>}
          >
            Time polls are unavailable right now.
          </Alert>
        ) : !polls.data ? (
          <Typography role="status" variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 0 } }}>
            Loading time polls…
          </Typography>
        ) : items.length > 0 ? (
          <Stack divider={<Divider />}>
            {items.map((poll) => (
              <PollRow
                key={poll.poll_id}
                poll={poll}
                votePoll={votePoll}
                convertPoll={convertPoll}
                onConvertRequest={setConverting}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 0 }, py: 1 }}>
            {canCreate
              ? "Poll the group on candidate times before scheduling the next event."
              : "No time polls right now."}
          </Typography>
        )}
      </Stack>

      <Stack component="section" spacing={0.5} aria-labelledby="community-reminder-settings">
        <Typography id="community-reminder-settings" component="h2" sx={{ ...headingSx, px: { xs: 0.5, sm: 0 } }}>
          Reminders
        </Typography>
        <ReminderPreference preferences={preferences} updatePreferences={updatePreferences} />
      </Stack>

      <CreatePollDialog
        open={creatingPoll}
        onClose={() => setCreatingPoll(false)}
        createPoll={createPoll}
        ownerFacilityId={ownerFacilityId}
      />
      <ConvertPollDialog poll={converting} onClose={() => setConverting(null)} convertPoll={convertPoll} />
    </Stack>
  );
}
